import {
  resolveFrontendEnvironmentPill,
  type FrontendEnvironmentPillConfig,
} from '@tov-plus/config';

export interface GuestEnv {
  apiBaseUrl: string;
  marketingSiteUrl: string;
  defaultOccasionSlug: string | null;
  defaultGuestToken: string | null;
  environmentPill: FrontendEnvironmentPillConfig | null;
}

export interface GuestEnvSource {
  VITE_API_BASE_URL?: string;
  VITE_MARKETING_SITE_URL?: string;
  VITE_GUEST_OCCASION_SLUG?: string;
  VITE_GUEST_TOKEN?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  PUBLIC_SENTRY_ENVIRONMENT?: string;
}

function normalizeBaseUrl(value: string): string {
  try {
    return new URL(value).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_API_BASE_URL must be a valid absolute URL. Received: ${value}`,
    );
  }
}

function normalizeOptionalValue(value: string | undefined): string | null {
  if (!value || value.trim().length === 0) {
    return null;
  }

  return value.trim();
}

function resolveApiBaseUrl(value: string | undefined, browserOrigin: string) {
  const explicitValue = normalizeOptionalValue(value);

  if (explicitValue) {
    return normalizeBaseUrl(explicitValue);
  }

  const currentUrl = new URL(browserOrigin);
  const { hostname, protocol } = currentUrl;

  if (hostname === '127.0.0.1' || hostname === 'localhost') {
    return 'http://127.0.0.1:3100';
  }

  if (hostname.endsWith('.tovplus.local')) {
    return `${protocol}//api.tovplus.local`;
  }

  if (
    hostname.startsWith('release-stage-') &&
    hostname.endsWith('.tovplus.com')
  ) {
    return `${protocol}//release-stage-api.tovplus.com`;
  }

  if (hostname === 'tov.plus' || hostname.endsWith('.tov.plus')) {
    return `${protocol}//api.tovplus.com`;
  }

  return 'http://127.0.0.1:3100';
}

function resolveBrowserOrigin(browserOrigin: string | undefined): string {
  return browserOrigin?.trim().length
    ? browserOrigin.trim()
    : typeof window === 'undefined'
      ? 'http://127.0.0.1:4176'
      : window.location.origin;
}

export function resolveMarketingSiteUrl(
  value: string | undefined,
  browserOrigin: string,
): string {
  const candidate =
    value && value.trim().length > 0
      ? value.trim()
      : (() => {
          const currentUrl = new URL(browserOrigin);
          const { hostname, protocol } = currentUrl;

          if (hostname === '127.0.0.1' || hostname === 'localhost') {
            return 'http://127.0.0.1:4321';
          }

          if (hostname.endsWith('.tovplus.local')) {
            return `${protocol}//landing.tovplus.local`;
          }

          if (
            hostname.startsWith('release-stage-') &&
            hostname.endsWith('.tovplus.com')
          ) {
            return `${protocol}//release-stage-landing.tovplus.com`;
          }

          return 'https://tovplus.com';
        })();

  try {
    return new URL(candidate).toString().replace(/\/$/, '');
  } catch {
    throw new Error(
      `VITE_MARKETING_SITE_URL must be a valid absolute URL. Received: ${candidate}`,
    );
  }
}

export function parseGuestEnv(
  source: GuestEnvSource,
  browserOrigin?: string,
): GuestEnv {
  const resolvedBrowserOrigin = resolveBrowserOrigin(browserOrigin);
  const apiBaseUrl = resolveApiBaseUrl(
    source.VITE_API_BASE_URL,
    resolvedBrowserOrigin,
  );

  return {
    apiBaseUrl,
    marketingSiteUrl: resolveMarketingSiteUrl(
      source.VITE_MARKETING_SITE_URL,
      resolvedBrowserOrigin,
    ),
    defaultOccasionSlug: normalizeOptionalValue(
      source.VITE_GUEST_OCCASION_SLUG,
    ),
    defaultGuestToken: normalizeOptionalValue(source.VITE_GUEST_TOKEN),
    environmentPill: resolveFrontendEnvironmentPill({
      apiBaseUrl,
      environmentName:
        source.VITE_SENTRY_ENVIRONMENT ?? source.PUBLIC_SENTRY_ENVIRONMENT,
      siteUrl: resolvedBrowserOrigin,
    }),
  };
}

export const guestEnv = parseGuestEnv(
  import.meta.env as unknown as GuestEnvSource,
);
